import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  vendorPortalApi,
  viewVendorPdf,
  downloadVendorPdf,
  type RequestDetail,
} from '../../features/vendorPortal/api.ts';
import { Icon } from '../../components/Icon.tsx';
import { t } from '../../lib/strings.ts';
import { formatDate } from '@/lib/format';

function fmtMvr(n: number | null | undefined): string {
  if (n == null) return '—';
  return `MVR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// request.status is the RAW request-status enum (DRAFT/SENT/CLOSED) — NOT the
// vendor won/lost/submitted/pending vocabulary used on the list pages. Label
// it with its own pill, matching RequestDetail.tsx's statusPill/statusLabel.
function reqStatusPill(status: string): string {
  switch (status) {
    case 'DRAFT': return 'pill pill-neutral';
    case 'SENT': return 'pill pill-pending';
    case 'CLOSED': return 'pill pill-success';
    default: return 'pill pill-neutral';
  }
}

function reqStatusLabel(status: string): string {
  switch (status) {
    case 'DRAFT': return t.vendorPortal.reqStatusDraft;
    case 'SENT': return t.vendorPortal.reqStatusSent;
    case 'CLOSED': return t.vendorPortal.reqStatusClosed;
    default: return status;
  }
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: boolean }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="status-label text-muted-foreground">{label}</span>
      <span className={['num tabular text-base font-semibold', accent ? 'text-[hsl(var(--success))]' : 'text-foreground'].join(' ')}>{value}</span>
    </div>
  );
}

function StatusPill({ done, doneLabel }: { done: boolean; doneLabel: string }): JSX.Element {
  return done
    ? <span className="pill pill-success">{doneLabel}</span>
    : <span className="pill pill-neutral">{t.vendorPortal.notYet}</span>;
}

function QuotationSection({ requestId, quotation }: { requestId: string; quotation: RequestDetail['quotation'] }): JSX.Element {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null);
  const [err, setErr] = useState(false);

  async function handleView() {
    setBusy('view'); setErr(false);
    try { await viewVendorPdf(vendorPortalApi.quotationPdfUrl(requestId)); }
    catch { setErr(true); }
    finally { setBusy(null); }
  }
  async function handleDownload() {
    setBusy('download'); setErr(false);
    try { await downloadVendorPdf(vendorPortalApi.quotationPdfUrl(requestId), `quotation-${requestId}.pdf`); }
    catch { setErr(true); }
    finally { setBusy(null); }
  }

  return (
    <section className="space-y-2">
      <div className="section-heading flex flex-wrap items-center justify-between gap-2">
        <h2 className="status-label text-muted-foreground">{t.vendorPortal.quotationHeading}</h2>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => void handleView()} disabled={busy !== null} className="btn-secondary btn-sm">
            {busy === 'view' ? t.vendorPortal.loading : t.vendorPortal.viewQuotation}
          </button>
          <button onClick={() => void handleDownload()} disabled={busy !== null} className="btn-secondary btn-sm">
            {busy === 'download' ? t.vendorPortal.loading : t.vendorPortal.downloadQuotation}
          </button>
        </div>
      </div>
      {err && <p className="text-sm text-destructive">{t.vendorPortal.pdfOpenError}</p>}

      {/* Mobile: stacked cards */}
      <ul className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] md:hidden">
        {quotation.lines.map((line, idx) => (
          <li key={idx} className="p-4">
            <p className="font-medium leading-snug">{line.name}</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span><span className="tabular text-foreground num">{line.qty}</span> {line.unit}</span>
              <span className="num tabular">{fmtMvr(line.unitPrice)}</span>
              <span className="num tabular font-semibold text-foreground">{fmtMvr(line.lineTotal)}</span>
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto rounded-3xl bg-[hsl(var(--chrome))] md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/[0.05] text-muted-foreground">
              <th className="px-4 py-3 text-start font-medium">{t.vendorPortal.colLineItem}</th>
              <th className="px-4 py-3 text-start font-medium">{t.vendorPortal.colLineQty}</th>
              <th className="px-4 py-3 text-start font-medium">{t.vendorPortal.colLineUnitPrice}</th>
              <th className="px-4 py-3 text-start font-medium">{t.vendorPortal.colLineTotal}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/[0.05]">
            {quotation.lines.map((line, idx) => (
              <tr key={idx} className="transition-colors hover:bg-[hsl(var(--surface-high))]">
                <td className="px-4 py-3 font-medium">{line.name}</td>
                <td className="px-4 py-3 tabular num">{line.qty} {line.unit}</td>
                <td className="px-4 py-3 tabular num">{fmtMvr(line.unitPrice)}</td>
                <td className="px-4 py-3 tabular num font-semibold">{fmtMvr(line.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-paper flex items-center gap-x-8 px-4 py-3">
        <Stat label={t.vendorPortal.grandTotal} value={fmtMvr(quotation.grandTotal)} accent />
      </div>
    </section>
  );
}

function AwardStatusSection({ award }: { award: NonNullable<RequestDetail['award']> }): JSX.Element {
  return (
    <section className="space-y-2">
      <div className="section-heading">
        <h2 className="status-label text-muted-foreground">{t.vendorPortal.awardStatusHeading}</h2>
      </div>
      <div className="card-paper flex flex-wrap items-center gap-x-8 gap-y-3 px-4 py-3.5">
        <Stat label={t.vendorPortal.awardedAt} value={formatDate(award.awardedAt)} />
        <div className="flex flex-col gap-1">
          <span className="status-label text-muted-foreground">{t.vendorPortal.sentToFinance}</span>
          <StatusPill done={award.sentToFinanceAt != null} doneLabel={formatDate(award.sentToFinanceAt)} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="status-label text-muted-foreground">{t.vendorPortal.goodsReceived}</span>
          <StatusPill done={award.goodsReceivedAt != null} doneLabel={formatDate(award.goodsReceivedAt)} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="status-label text-muted-foreground">{t.vendorPortal.invoiceConfirmed}</span>
          <StatusPill done={award.invoice.confirmed} doneLabel={formatDate(award.invoice.confirmedAt)} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="status-label text-muted-foreground">{t.vendorPortal.paymentHeading}</span>
          <StatusPill done={award.payment.paid} doneLabel={formatDate(award.payment.paidAt)} />
        </div>
      </div>
      {award.invoice.confirmed && (award.invoice.confirmedBankName || award.invoice.confirmedBankNumber) && (
        <div className="card-paper flex flex-col gap-1 px-4 py-3">
          <span className="status-label text-muted-foreground">{t.vendorPortal.confirmedBankAccount}</span>
          <span className="text-sm">
            {award.invoice.confirmedBankName ?? '—'}
            {award.invoice.confirmedBankNumber && <span className="num ms-2 mono-meta" dir="ltr">{award.invoice.confirmedBankNumber}</span>}
          </span>
        </div>
      )}
    </section>
  );
}

function InvoiceSection({ requestId, award }: { requestId: string; award: NonNullable<RequestDetail['award']> }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function handleView() {
    setBusy(true); setErr(false);
    try { await viewVendorPdf(vendorPortalApi.invoicePdfUrl(requestId)); }
    catch { setErr(true); }
    finally { setBusy(false); }
  }

  return (
    <section className="space-y-2">
      <div className="section-heading">
        <h2 className="status-label text-muted-foreground">{t.vendorPortal.invoiceHeading}</h2>
      </div>
      {award.invoice.confirmed ? (
        <div className="card-paper flex flex-wrap items-center justify-between gap-3 p-4">
          <span className="text-sm text-muted-foreground">
            {t.vendorPortal.invoiceConfirmed}: <span className="num">{formatDate(award.invoice.confirmedAt)}</span>
          </span>
          <button onClick={() => void handleView()} disabled={busy} className="btn-secondary btn-sm">
            {busy ? t.vendorPortal.loading : t.vendorPortal.viewConfirmedInvoice}
          </button>
        </div>
      ) : (
        <p className="card-paper p-4 text-sm text-muted-foreground">{t.vendorPortal.invoiceNotConfirmed}</p>
      )}
      {err && <p className="text-sm text-destructive">{t.vendorPortal.pdfOpenError}</p>}
    </section>
  );
}

function PaymentSection({ requestId, award }: { requestId: string; award: NonNullable<RequestDetail['award']> }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function handleView() {
    setBusy(true); setErr(false);
    try { await viewVendorPdf(vendorPortalApi.slipUrl(requestId)); }
    catch { setErr(true); }
    finally { setBusy(false); }
  }

  return (
    <section className="space-y-2">
      <div className="section-heading">
        <h2 className="status-label text-muted-foreground">{t.vendorPortal.paymentHeading}</h2>
      </div>
      {award.payment.paid ? (
        <div className="card-paper flex flex-wrap items-center justify-between gap-3 p-4">
          <span className="text-sm text-muted-foreground">
            {t.vendorPortal.paidAt}: <span className="num">{formatDate(award.payment.paidAt)}</span>
          </span>
          {award.payment.hasSlip && (
            <button onClick={() => void handleView()} disabled={busy} className="btn-secondary btn-sm">
              {busy ? t.vendorPortal.loading : t.vendorPortal.viewSlip}
            </button>
          )}
        </div>
      ) : (
        <p className="card-paper p-4 text-sm text-muted-foreground">{t.vendorPortal.notYetPaid}</p>
      )}
      {err && <p className="text-sm text-destructive">{t.vendorPortal.pdfOpenError}</p>}
    </section>
  );
}

export function VendorRequestDetail(): JSX.Element {
  const { requestId } = useParams({ from: '/vendor/requests/$requestId' });
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vendor-request-detail', requestId],
    queryFn: () => vendorPortalApi.requestDetail(requestId),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="page">
        <p className="status-label text-muted-foreground" role="status">{t.vendorPortal.detailLoading}</p>
      </div>
    );
  }

  if (isError || !data) {
    const notFound = (error as { response?: { status?: number } } | undefined)?.response?.status === 404;
    return (
      <div className="page space-y-4">
        <button
          onClick={() => void navigate({ to: '/vendor' })}
          className="btn-ghost btn-sm"
          aria-label={t.vendorPortal.ariaBack}
        >
          <Icon name="arrow_forward" size={18} />
        </button>
        <p className="text-sm text-destructive">
          {notFound ? t.vendorPortal.detailNotFound : t.vendorPortal.detailError}
        </p>
      </div>
    );
  }

  const { request, quotation, award } = data;

  return (
    <div className="page space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-start gap-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <button
          onClick={() => void navigate({ to: '/vendor' })}
          className="btn-ghost btn-sm mt-0.5"
          aria-label={t.vendorPortal.ariaBack}
        >
          <Icon name="arrow_forward" size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <span className="mono-meta text-muted-foreground" dir="ltr">{request.refNumber}</span>
            <span className={reqStatusPill(request.status)}>{reqStatusLabel(request.status)}</span>
          </div>
          <h1 className="text-2xl font-bold text-ink leading-tight">{request.title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>{t.vendorPortal.submittedAt} <span className="num">{formatDate(quotation.submittedAt)}</span></span>
            {request.deadline && (
              <span>{t.vendorPortal.detailDeadline}: <span className="num">{formatDate(request.deadline)}</span></span>
            )}
          </div>
        </div>
      </header>

      <QuotationSection requestId={requestId} quotation={quotation} />

      {award ? (
        <>
          <AwardStatusSection award={award} />
          <InvoiceSection requestId={requestId} award={award} />
          <PaymentSection requestId={requestId} award={award} />
        </>
      ) : (
        <p className="card-paper p-4 text-sm text-muted-foreground">{t.vendorPortal.noAwardYet}</p>
      )}
    </div>
  );
}
